import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { industriesQueryOptions } from "@/lib/queries/opportunity-sets.queries";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import type { CompetitorFiltersInput, MyBrandFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  drilldownDataInfiniteQueryOptions,
  drilldownMyBrandDataInfiniteQueryOptions,
} from "@/lib/queries/drilldown.queries";
import {
  useDrilldownFiltersStore,
  OBJECT_TYPE_OPTIONS,
  EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_OBJECT_TYPE_OPTIONS,
  MY_BRAND_EFFECTIVE_STATUS_OPTIONS,
} from "../stores/drilldownFilters.store";
import { useDrilldownSearchSync } from "../hooks/useDrilldownSearchSync";
import { resolveDashboardRequestWindow } from "@/features/dashboard/lib/resolve-dashboard-request-window";
import {
  resolveLookbackSync,
  type ModeSelection,
} from "../lib/lookback-date-window";
import { isDrilldownDataQueryEnabled } from "../lib/is-drilldown-data-query-enabled";
import { toDrilldownCompetitorQueryFilters } from "../lib/to-drilldown-competitor-query-filters";
import { DrilldownViewModeToggle } from "./DrilldownViewModeToggle";
import { DrilldownGridView } from "./DrilldownGridView"
import { DrilldownMyBrandGridView } from "./DrilldownMyBrandGridView"
import { DrilldownListView } from "./DrilldownListView";
import { DrilldownChartView } from "./DrilldownChartView";
import { DrilldownMyBrandChartView } from "./DrilldownMyBrandChartView";
import { DrilldownCreativeDrawer } from "./drawer/DrilldownCreativeDrawer";
import { DrilldownMyBrandCreativeDrawer } from "./drawer/DrilldownMyBrandCreativeDrawer";
import { mapCompetitorCardRow } from "../lib/drilldown-competitor-card-mapper";
import { useCreativeUrlSettings } from "@/lib/queries/system-settings.queries";
import { mapMyBrandCardRow } from "../lib/drilldown-my-brand-card-mapper";
import type { CompetitorCreativeCardModel, MyBrandCreativeCardModel } from "../lib/drilldown-card.types";
import { useDrilldownCreativeCache } from "../hooks/useDrilldownCreativeCache";
import { useDrilldownMyBrandCreativeCache } from "../hooks/useDrilldownMyBrandCreativeCache";
import { COMPETITOR_LEADING_COLUMNS, COMPETITOR_TRAILING_COLUMNS } from "../lib/competitor-list-columns";
import { MY_BRAND_LEADING_COLUMNS, buildMyBrandTrailingColumns } from "../lib/my-brand-list-columns";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";
import { useTenantCurrency } from "@/lib/use-tenant-currency";
import { useResolvedMyBrandKpis } from "../hooks/useResolvedMyBrandKpis";

export function DrilldownPage() {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const { myBrandById } = useKpiSelectorCatalog("drilldown");
  const resolvedMyBrandKpis = useResolvedMyBrandKpis();
  const { symbol: currencySymbol } = useTenantCurrency();
  const { competitorsPath } = useCreativeUrlSettings();
  const { mode, appliedCompetitorFilters, appliedMyBrandFilters } = useDashboardFiltersStore();
  const storeTenantId = useDashboardFiltersStore((s) => s._activeTenantId);
  const viewMode = useDrilldownFiltersStore((s) => s.viewMode);
  const {
    appliedObjectType,
    appliedEffectiveStatus,
    appliedMyBrandObjectType,
    appliedMyBrandEffectiveStatus,
    appliedStartDate,
    appliedEndDate,
    appliedMyBrandStartDate,
    appliedMyBrandEndDate,
    appliedSegment1,
    appliedSegment2,
    appliedSegment3,
    appliedMyBrandSegment1,
    appliedMyBrandSegment2,
    appliedMyBrandSegment3,
    competitorSortMode,
    competitorSortDirection,
    myBrandKpis,
    myBrandHiddenColumns,
    myBrandSortMode,
    myBrandSortDirection,
    appliedAudienceSegment,
    appliedDevice,
    initializeDateRange,
  } = useDrilldownFiltersStore();

  // Collapse the two mode-namespaced sort pairs into the single sortMode/sortDirection
  // value the API actually receives — this is the only place that happens.
  const sortMode = mode === "my-brand" ? myBrandSortMode : competitorSortMode;
  const sortDirection = mode === "my-brand" ? myBrandSortDirection : competitorSortDirection;

  // Same collapse for Ad Type / Status — each mode has its own value domain (see
  // MY_BRAND_OBJECT_TYPE_OPTIONS / MY_BRAND_EFFECTIVE_STATUS_OPTIONS).
  const objectType = mode === "my-brand" ? appliedMyBrandObjectType : appliedObjectType;
  const effectiveStatus = mode === "my-brand" ? appliedMyBrandEffectiveStatus : appliedEffectiveStatus;
  const objectTypeOptionsCount =
    mode === "my-brand" ? MY_BRAND_OBJECT_TYPE_OPTIONS.length : OBJECT_TYPE_OPTIONS.length;
  const effectiveStatusOptionsCount =
    mode === "my-brand" ? MY_BRAND_EFFECTIVE_STATUS_OPTIONS.length : EFFECTIVE_STATUS_OPTIONS.length;

  // Same collapse for Group By segments — namespaced per mode (own remembered selection).
  const segment1 = mode === "my-brand" ? appliedMyBrandSegment1 : appliedSegment1;
  const segment2 = mode === "my-brand" ? appliedMyBrandSegment2 : appliedSegment2;
  const segment3 = mode === "my-brand" ? appliedMyBrandSegment3 : appliedSegment3;

  // Same collapse for date range — namespaced per mode (own remembered range).
  const startDate = mode === "my-brand" ? appliedMyBrandStartDate : appliedStartDate;
  const endDate = mode === "my-brand" ? appliedMyBrandEndDate : appliedEndDate;

  const { pushAppliedToUrl, urlHydrationPhase, parsedSearch, externalHydrationNonce } =
    useDrilldownSearchSync();

  // Keep a ref so the date-init effect always reads the latest URL dates without
  // adding parsedSearch to its dependency array (which would re-run on every Apply).
  const parsedSearchRef = useRef(parsedSearch);
  parsedSearchRef.current = parsedSearch;

  // Seeded from the first *hydrated* applied selections — not the store default —
  // so URL hydration is not mistaken for a user changing the period.
  const prevLookbackRef = useRef<{
    competitors?: ModeSelection;
    myBrand?: ModeSelection;
  }>({});

  // Tracks the last external-hydration nonce this effect has consumed, so a fresh
  // back/forward/paste hydration is detected exactly once.
  const lastHandledHydrationNonceRef = useRef(externalHydrationNonce);

  // Push viewMode / sort / KPI selection / hidden date columns to the URL immediately
  // (no Apply step). KPI and column picks have to land in the URL or a reload
  // brings the removed fields back.
  useEffect(() => {
    if (urlHydrationPhase === "pending") return;
    pushAppliedToUrl();
  }, [viewMode, sortMode, sortDirection, myBrandKpis, myBrandHiddenColumns, urlHydrationPhase, pushAppliedToUrl]);

  // Each mode's calendar bounds/default derive from that mode's OWN dashboard-level
  // lookback selector (My Brand's dateRange dropdown is independent of Competitors')
  // — mirrors GlobalFilterHeader.tsx's `activeDateRange` mode resolution.

  // Sync BOTH modes' drilldown date ranges whenever either mode's global lookback
  // window changes. Seeds both pairs unconditionally in one commit (like
  // hydrateFromApplied) rather than only the active mode — switching modes must
  // never leave the other mode's dates uninitialized. Depends on the two raw
  // lookback sources directly (not the derived, mode-conditional globalDateRange
  // or `mode` itself) so a mode switch alone never re-runs or clobbers either pair.
  //
  // Deep-link path (`pending`): URL dates win. After hydration, only a real
  // lookback change overwrites that mode; the other mode keeps its applied
  // selection. External history navigation (back/forward/paste) restores custom
  // dates from the URL, so it is re-baselined and skipped rather than recomputed.
  // handleApply pushes URL *before* this effect commits new dates, so the
  // lookback-change push lives here (replace, and only when dates differ).
  useEffect(() => {
    // Store dates win; catalog presets resolve when dates are empty (URL reload).
    const competitorsResolved = resolveDashboardRequestWindow(appliedCompetitorFilters);
    const myBrandResolved = resolveDashboardRequestWindow(appliedMyBrandFilters);
    const competitorsWindow = {
      start: competitorsResolved.startDate,
      end: competitorsResolved.endDate,
    };
    const myBrandWindow = {
      start: myBrandResolved.startDate,
      end: myBrandResolved.endDate,
    };
    const competitorsSelection = {
      presetId: appliedCompetitorFilters.activePresetId,
      start: appliedCompetitorFilters.startDate,
      end: appliedCompetitorFilters.endDate,
    };
    const myBrandSelection = {
      presetId: appliedMyBrandFilters.activePresetId,
      start: appliedMyBrandFilters.startDate,
      end: appliedMyBrandFilters.endDate,
    };

    const isExternalHydration = externalHydrationNonce !== lastHandledHydrationNonceRef.current;
    lastHandledHydrationNonceRef.current = externalHydrationNonce;

    const state = useDrilldownFiltersStore.getState();
    const search = parsedSearchRef.current;

    const action = resolveLookbackSync({
      phase: urlHydrationPhase,
      isExternalHydration,
      prevCompetitorsSelection: prevLookbackRef.current.competitors,
      prevMyBrandSelection: prevLookbackRef.current.myBrand,
      competitorsSelection: competitorsSelection,
      myBrandSelection: myBrandSelection,
      competitorsWindow,
      myBrandWindow,
      currentCompetitors: { start: state.appliedStartDate, end: state.appliedEndDate },
      currentMyBrand: { start: state.appliedMyBrandStartDate, end: state.appliedMyBrandEndDate },
      urlCompetitors: { start: search.startDate, end: search.endDate },
      urlMyBrand: { start: search.myBrandStartDate, end: search.myBrandEndDate },
    });

    if (action.type === "noop") return;

    if (action.type === "seed") {
      prevLookbackRef.current = {
        competitors: competitorsSelection,
        myBrand: myBrandSelection,
      };
      return;
    }

    if (action.type === "initialize") {
      initializeDateRange(
        action.competitors.start,
        action.competitors.end,
        action.myBrand.start,
        action.myBrand.end,
      );
      // So a later explore hydration (custom dates landing after this run) is an
      // `apply`, not a `seed` that would leave the token fallback in place.
      prevLookbackRef.current = {
        competitors: competitorsSelection,
        myBrand: myBrandSelection,
      };
      return;
    }

    initializeDateRange(
      action.competitors.start,
      action.competitors.end,
      action.myBrand.start,
      action.myBrand.end,
    );
    prevLookbackRef.current = {
      competitors: competitorsSelection,
      myBrand: myBrandSelection,
    };
    if (action.pushUrl) {
      pushAppliedToUrl(true);
    }
    // The selection fields are load-bearing deps, not just the token: a preset
    // with no legacy token leaves `dateRange` untouched, so without them this
    // effect never re-runs and a season pick never reaches the store.
  }, [
    appliedCompetitorFilters.dateRange,
    appliedCompetitorFilters.activePresetId,
    appliedCompetitorFilters.startDate,
    appliedCompetitorFilters.endDate,
    appliedMyBrandFilters.dateRange,
    appliedMyBrandFilters.activePresetId,
    appliedMyBrandFilters.startDate,
    appliedMyBrandFilters.endDate,
    urlHydrationPhase,
    externalHydrationNonce,
    initializeDateRange,
    pushAppliedToUrl,
  ]);

  const { data: industries = [], isLoading: industriesLoading } = useQuery(
    industriesQueryOptions("drilldown", activeTenantId),
  );

  const competitorFilters = useMemo<CompetitorFiltersInput>(
    () => toDrilldownCompetitorQueryFilters(appliedCompetitorFilters as CompetitorFiltersInput),
    [appliedCompetitorFilters],
  );

  // Normalize the store's My Brand filters for the API contract (arrays only):
  // null (All) → [] so the query builder omits the param and the backend applies
  // its "All" bypass. Mirrors the dashboard My Brand normalization.
  const myBrandFilters = useMemo<MyBrandFiltersInput>(() => {
    return {
      ...appliedMyBrandFilters,
      platforms: appliedMyBrandFilters.platforms ?? [],
      accounts: appliedMyBrandFilters.accounts ?? [],
      products: appliedMyBrandFilters.products ?? [],
      dateRange: appliedMyBrandFilters.dateRange as MyBrandFiltersInput["dateRange"],
    };
  }, [appliedMyBrandFilters]);

  // Advanced filters are namespaced per mode (own field vocabulary) — same collapse as
  // sortMode/sortDirection and Ad Type/Status above.
  const appliedCompetitorAdvancedFilters = useDrilldownFiltersStore((s) => s.appliedAdvancedFilters);
  const appliedMyBrandAdvancedFilters = useDrilldownFiltersStore((s) => s.appliedMyBrandAdvancedFilters);
  const appliedAdvancedFilters = mode === "my-brand" ? appliedMyBrandAdvancedFilters : appliedCompetitorAdvancedFilters;

  const drilldownFilters = useMemo(
    () => ({
      objectType: objectType.length >= objectTypeOptionsCount ? [] : objectType,
      effectiveStatus: effectiveStatus.length >= effectiveStatusOptionsCount ? [] : effectiveStatus,
      audienceSegment: appliedAudienceSegment,
      device: mode === "my-brand" ? appliedDevice : [],
      startDate,
      endDate,
      segment1,
      segment2,
      segment3,
      sortMode,
      sortDirection,
      advancedFilters: appliedAdvancedFilters,
    }),
    [
      objectType,
      effectiveStatus,
      appliedAudienceSegment,
      appliedDevice,
      mode,
      objectTypeOptionsCount,
      effectiveStatusOptionsCount,
      startDate,
      endDate,
      segment1,
      segment2,
      segment3,
      sortMode,
      sortDirection,
      appliedAdvancedFilters,
    ],
  );

  // Store tenant updates only inside resetForTenant (same set as the product
  // write). Drilldown URL phase does not reset on tenant switch, so a render can
  // still have activeTenantId B with tenant A's products.
  const competitorsEnabled = isDrilldownDataQueryEnabled({
    pageMode: mode,
    queryMode: "competitors",
    urlHydrationPhase,
    storeTenantId,
    activeTenantId,
  });
  const myBrandEnabled = isDrilldownDataQueryEnabled({
    pageMode: mode,
    queryMode: "my-brand",
    urlHydrationPhase,
    storeTenantId,
    activeTenantId,
  });
  const enabled = competitorsEnabled || myBrandEnabled;

  const competitorQuery = useInfiniteQuery({
    ...drilldownDataInfiniteQueryOptions(
      competitorsEnabled ? activeTenantId : null,
      competitorFilters,
      drilldownFilters,
    ),
  });
  const myBrandQuery = useInfiniteQuery({
    ...drilldownMyBrandDataInfiniteQueryOptions(
      myBrandEnabled ? activeTenantId : null,
      myBrandFilters,
      drilldownFilters,
    ),
  });

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = mode === "my-brand" ? myBrandQuery : competitorQuery;

  // Pages append in global sort order — flatten straight through (no re-sort).
  const items = useMemo(() => data?.pages.flatMap((p) => p.rows) ?? [], [data]);
  const listTotals = data?.pages[0]?.totals ?? null;

  const [selectedCreativeId, setSelectedCreativeId] = useState<string | null>(null);
  const [selectedMyBrandCreativeId, setSelectedMyBrandCreativeId] = useState<string | null>(null);
  const { put: cacheCreative, get: getCachedCreative } = useDrilldownCreativeCache();
  const { put: cacheMyBrandCreative, get: getCachedMyBrandCreative } = useDrilldownMyBrandCreativeCache();

  useEffect(() => {
    setSelectedCreativeId(null);
    setSelectedMyBrandCreativeId(null);
  }, [mode]);

  const competitorCardModels = useMemo<CompetitorCreativeCardModel[]>(
    () =>
      mode === "competitors"
        ? items.map((row, index) => mapCompetitorCardRow(row, index, competitorsPath))
        : [],
    [items, mode, competitorsPath],
  );

  const myBrandCardModels = useMemo<MyBrandCreativeCardModel[]>(
    () => (mode === "my-brand" ? items.map((row, index) => mapMyBrandCardRow(row, resolvedMyBrandKpis, myBrandById, index, currencySymbol)) : []),
    [items, mode, resolvedMyBrandKpis, myBrandById, currencySymbol],
  );

  const selectedCreative = useMemo(() => {
    if (!selectedCreativeId) return null;
    const cached = getCachedCreative(selectedCreativeId);
    if (cached) return cached;
    return competitorCardModels.find((c) => c.id === selectedCreativeId) ?? null;
  }, [selectedCreativeId, getCachedCreative, competitorCardModels]);

  useEffect(() => {
    if (selectedCreativeId !== null && selectedCreative === null) {
      setSelectedCreativeId(null);
    }
  }, [selectedCreativeId, selectedCreative]);

  const handleOpenDrawer = useCallback(
    (id: string) => {
      const fromList = competitorCardModels.find((c) => c.id === id);
      const cached = getCachedCreative(id);
      const model = fromList ?? cached;
      if (!model) return;
      if (fromList) cacheCreative(fromList);
      setSelectedCreativeId(id);
    },
    [competitorCardModels, cacheCreative, getCachedCreative],
  );

  const handleRowDrawerClick = useCallback(
    (row: Record<string, unknown>, index: number) => {
      const model = mapCompetitorCardRow(row, index, competitorsPath);
      cacheCreative(model);
      setSelectedCreativeId(model.id);
    },
    [competitorsPath, cacheCreative],
  );

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) setSelectedCreativeId(null);
  }, []);

  const selectedMyBrandCreative = useMemo(() => {
    if (!selectedMyBrandCreativeId) return null;
    const cached = getCachedMyBrandCreative(selectedMyBrandCreativeId);
    if (cached) return cached;
    return myBrandCardModels.find((c) => c.id === selectedMyBrandCreativeId) ?? null;
  }, [selectedMyBrandCreativeId, getCachedMyBrandCreative, myBrandCardModels]);

  useEffect(() => {
    if (selectedMyBrandCreativeId !== null && selectedMyBrandCreative === null) {
      setSelectedMyBrandCreativeId(null);
    }
  }, [selectedMyBrandCreativeId, selectedMyBrandCreative]);

  const handleOpenMyBrandDrawer = useCallback(
    (id: string) => {
      const fromList = myBrandCardModels.find((c) => c.id === id);
      const cached = getCachedMyBrandCreative(id);
      const model = fromList ?? cached;
      if (!model) return;
      if (fromList) cacheMyBrandCreative(fromList);
      setSelectedMyBrandCreativeId(id);
    },
    [myBrandCardModels, cacheMyBrandCreative, getCachedMyBrandCreative],
  );

  const handleMyBrandRowDrawerClick = useCallback(
    (row: Record<string, unknown>, index: number) => {
      const model = mapMyBrandCardRow(row, resolvedMyBrandKpis, myBrandById, index, currencySymbol);
      cacheMyBrandCreative(model);
      setSelectedMyBrandCreativeId(model.id);
    },
    [cacheMyBrandCreative, resolvedMyBrandKpis, myBrandById, currencySymbol],
  );

  const handleMyBrandDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) setSelectedMyBrandCreativeId(null);
  }, []);

  // My Brand's visible KPI columns/order are driven by the KPI pill row's selection —
  // replaces the old static MY_BRAND_TRAILING_COLUMNS export.
  const myBrandTrailingColumns = useMemo(
    () => buildMyBrandTrailingColumns(resolvedMyBrandKpis, myBrandById, currencySymbol, myBrandHiddenColumns, t),
    [resolvedMyBrandKpis, myBrandById, currencySymbol, myBrandHiddenColumns, t],
  );

  const paginationHasNextPage = !!hasNextPage;

  if (!industriesLoading && industries.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
          <AlertTriangle className="h-7 w-7 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-foreground">No industries found</h2>
          <p className="text-sm text-muted-foreground">
            Please contact administration to set up industry data for your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
      <DrilldownViewModeToggle
        rows={items}
        hasNextPage={paginationHasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      {enabled && isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error?.message ?? "Drilldown request failed."}
        </p>
      )}

      {enabled && isLoading && viewMode === "grid" && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-md border bg-card"
              style={{ "--shimmer-delay": `${i * 80}ms` } as React.CSSProperties}
            >
              <div className="skeleton-shimmer-bar aspect-[4/5] w-full" />
              <div className="flex gap-1.5 p-2">
                <div className="skeleton-shimmer-bar h-3 w-14 rounded-md" />
                <div className="skeleton-shimmer-bar h-3 w-10 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      )}

      {enabled && isLoading && viewMode === "list" && (
        <div className="min-h-0 flex-1 overflow-auto rounded-md border bg-card">
          <div className="min-w-[860px]">
            <div className="sticky top-0 z-10 flex items-center gap-4 border-b bg-muted/50 px-3 py-2.5">
              <div className="w-[52px] shrink-0" />
              <div className="skeleton-shimmer-bar h-3 w-14 rounded" />
              <div className="skeleton-shimmer-bar h-3 w-16 rounded" />
              <div className="skeleton-shimmer-bar h-3 w-16 rounded" />
              <div className="ml-auto skeleton-shimmer-bar h-3 w-10 rounded" />
              <div className="skeleton-shimmer-bar h-3 w-12 rounded" />
              <div className="skeleton-shimmer-bar h-3 w-20 rounded" />
            </div>
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-4 border-b px-3 py-2"
                style={{ "--shimmer-delay": `${i * 50}ms` } as React.CSSProperties}
              >
                <div className="skeleton-shimmer-bar h-14 w-[52px] shrink-0 rounded-md" />
                <div className={`skeleton-shimmer-bar h-3 rounded ${["w-28","w-36","w-24","w-32","w-20","w-40","w-28","w-24","w-36","w-20","w-32","w-28"][i]}`} />
                <div className="skeleton-shimmer-bar h-3 w-20 rounded" />
                <div className="skeleton-shimmer-bar h-3 w-20 rounded" />
                <div className="ml-auto skeleton-shimmer-bar h-3 w-10 rounded" />
                <div className="skeleton-shimmer-bar h-3 w-12 rounded" />
                <div className="skeleton-shimmer-bar h-3 w-14 rounded" />
              </div>
            ))}
          </div>
        </div>
      )}

      {enabled && isLoading && viewMode === "chart" && (
        <div className="min-h-0 flex-1 overflow-auto rounded-md border bg-card p-4">
          <div className="min-w-[640px]">
            <div className="flex items-end gap-2" style={{ height: 420 }}>
              {([65, 40, 80, 28, 58, 92, 45, 72, 35, 60, 85, 50] as const).map((h, i) => (
                <div
                  key={i}
                  className="skeleton-shimmer-bar flex-1 rounded-t-lg"
                  style={{ height: `${h}%`, "--shimmer-delay": `${i * 60}ms` } as React.CSSProperties}
                />
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="skeleton-shimmer-bar flex-1 rounded-lg"
                  style={{ height: 96, "--shimmer-delay": `${i * 60}ms` } as React.CSSProperties}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {competitorsEnabled && !isLoading && !isError && viewMode === "grid" && (
        <DrilldownGridView
          rows={items}
          loadedCount={items.length}
          fetchNextPage={fetchNextPage}
          hasNextPage={paginationHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onOpenDrawer={handleOpenDrawer}
        />
      )}

      {myBrandEnabled && !isLoading && !isError && viewMode === "grid" && (
        <DrilldownMyBrandGridView
          rows={items}
          loadedCount={items.length}
          fetchNextPage={fetchNextPage}
          hasNextPage={paginationHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          myBrandKpis={resolvedMyBrandKpis}
          onOpenDrawer={handleOpenMyBrandDrawer}
          showAdSpend={segment1 === "Hash"}
          myBrandFilters={myBrandFilters}
          drilldownFilters={drilldownFilters}
        />
      )}

      {competitorsEnabled && !isLoading && !isError && viewMode === "list" && (
        <DrilldownListView
          rows={items}
          loadedCount={items.length}
          fetchNextPage={fetchNextPage}
          hasNextPage={paginationHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          segment1={segment1}
          segment2={segment2}
          segment3={segment3}
          leadingColumns={COMPETITOR_LEADING_COLUMNS}
          trailingColumns={COMPETITOR_TRAILING_COLUMNS}
          totals={listTotals}
          onRowDrawerClick={handleRowDrawerClick}
        />
      )}

      {myBrandEnabled && !isLoading && !isError && viewMode === "list" && (
        <DrilldownListView
          rows={items}
          loadedCount={items.length}
          fetchNextPage={fetchNextPage}
          hasNextPage={paginationHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          segment1={segment1}
          segment2={segment2}
          segment3={segment3}
          leadingColumns={MY_BRAND_LEADING_COLUMNS}
          trailingColumns={myBrandTrailingColumns}
          totals={listTotals}
          onRowDrawerClick={handleMyBrandRowDrawerClick}
        />
      )}

      {competitorsEnabled && !isLoading && !isError && viewMode === "chart" && (
        <DrilldownChartView
          rows={items}
          sortMode={competitorSortMode}
          segment1={segment1}
          segment2={segment2}
          segment3={segment3}
          fetchNextPage={fetchNextPage}
          hasNextPage={paginationHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onBarDrawerClick={handleRowDrawerClick}
        />
      )}

      {myBrandEnabled && !isLoading && !isError && viewMode === "chart" && (
        <DrilldownMyBrandChartView
          rows={items}
          sortMode={myBrandSortMode}
          segment1={segment1}
          segment2={segment2}
          segment3={segment3}
          fetchNextPage={fetchNextPage}
          hasNextPage={paginationHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onBarDrawerClick={handleMyBrandRowDrawerClick}
        />
      )}

      <DrilldownCreativeDrawer
        scope="drilldown"
        creative={selectedCreative}
        open={selectedCreative !== null}
        onOpenChange={handleDrawerOpenChange}
        startDate={appliedStartDate}
        endDate={appliedEndDate}
        dateRange={appliedCompetitorFilters.dateRange}
      />

      <DrilldownMyBrandCreativeDrawer
        creative={selectedMyBrandCreative}
        open={selectedMyBrandCreative !== null}
        onOpenChange={handleMyBrandDrawerOpenChange}
        myBrandFilters={myBrandFilters}
        drilldownFilters={drilldownFilters}
      />
    </div>
  );
}
